import React, { useState } from 'react';
import {
  Box, Avatar, Typography, Link as MuiLink, Button, Stack, CircularProgress,
} from '@mui/material';
import { Circle as CircleIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useTheme } from '@mui/material/styles';

const initialsFromName = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

function ProposalReinviteNotificationCard({ notification, onMarkRead, handleNotificationAction }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const payload = notification.payload || {};

  const consortiumId = payload.consortium_id;
  const consortiumName = payload.consortium_name || 'a project';
  const fromUserName = payload.from_user_name || 'Someone';
  const fromUserId = payload.from_user_id;
  const roleId = payload.role_id;
  const fromUserAvatarSrc = payload.from_user_avatar || payload.from_user_avatar_url || null;

  const [loading, setLoading] = useState(false);
  const [imgBroken, setImgBroken] = useState(false);
  const showInitials = !fromUserAvatarSrc || imgBroken;

  const consortiumUrl = consortiumId ? `/consortium/${consortiumId}` : null;
  const senderProfileUrl = fromUserId ? `/profile/${fromUserId}` : null;

  const handleMarkRead = () => {
    if (!notification.is_read && onMarkRead) onMarkRead();
  };

  const handleCardClick = () => {
    handleMarkRead();
    if (consortiumUrl) navigate(consortiumUrl);
  };

  const handleSenderClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleMarkRead();
    if (senderProfileUrl) navigate(senderProfileUrl);
  };

  const handleRejoin = async (e) => {
    e.stopPropagation();
    if (!roleId) return;
    setLoading(true);
    try {
      await handleNotificationAction(notification.id, {
        action: 'accept_proposal_reinvite',
        meta: { role_id: roleId },
      });
    } catch (error) {
      console.error('Rejoin failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleIgnore = async (e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      await handleNotificationAction(notification.id, { action: 'ignore_invitation' });
    } catch {
      if (onMarkRead) onMarkRead();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      onClick={handleCardClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
        transition: 'background-color 0.2s',
        cursor: consortiumUrl ? 'pointer' : 'default',
        '&:hover': { backgroundColor: 'action.selected' },
      }}
    >
      <Avatar
        src={showInitials ? undefined : fromUserAvatarSrc}
        onError={() => setImgBroken(true)}
        sx={{
          width: 32,
          height: 32,
          backgroundColor: theme.palette.primary.avatar,
          color: 'white',
          cursor: senderProfileUrl ? 'pointer' : 'default',
          flexShrink: 0,
          fontSize: 13,
        }}
        onClick={senderProfileUrl ? handleSenderClick : undefined}
      >
        {initialsFromName(fromUserName)}
      </Avatar>

      <Typography
        variant="body2"
        sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}
      >
        {senderProfileUrl ? (
          <MuiLink
            component="a"
            href={senderProfileUrl}
            underline="hover"
            color="text.primary"
            fontWeight={600}
            onClick={handleSenderClick}
            sx={{ cursor: 'pointer' }}
          >
            {fromUserName}
          </MuiLink>
        ) : (
          <Box component="span" fontWeight={600}>{fromUserName}</Box>
        )}
        <Box component="span" color="text.secondary">
          invited you to rejoin{' '}
          <Box component="span" fontWeight={500} color="text.primary">
            {consortiumName}
          </Box>
        </Box>
      </Typography>

      <Stack
        direction="row"
        spacing={1}
        sx={{ flexShrink: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          size="small"
          variant="contained"
          color="success"
          onClick={handleRejoin}
          disabled={loading || !roleId}
          sx={{ minWidth: 70 }}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : 'Rejoin'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          onClick={handleIgnore}
          disabled={loading}
          sx={{ minWidth: 70 }}
        >
          Ignore
        </Button>
      </Stack>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatDistanceToNow(new Date(notification.created), { addSuffix: true })}
        </Typography>
        {!notification.is_read && (
          <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />
        )}
      </Box>
    </Box>
  );
}

export default ProposalReinviteNotificationCard;
